/**
 * 国际化错误处理器
 * 处理国际化相关的各种错误情况，确保系统稳定性
 */

// 国际化相关错误类型常量
export const I18N_ERROR_TYPES = {
  // 初始化相关错误
  INITIALIZATION_FAILED: 'i18n_init_failed',
  FRAMEWORK_LOAD_ERROR: 'framework_load_error',
  CONFIG_INVALID: 'config_invalid',
  
  // 翻译资源相关错误
  TRANSLATION_MISSING: 'translation_missing',
  RESOURCE_LOAD_FAILED: 'resource_load_failed',
  LOCALE_NOT_FOUND: 'locale_not_found',
  
  // 语言设置相关错误
  LANGUAGE_NOT_SUPPORTED: 'language_not_supported',
  LANGUAGE_SWITCH_FAILED: 'language_switch_failed',
  URL_PARSE_ERROR: 'url_parse_error',
  
  // 运行时错误
  TRANSLATION_FUNCTION_ERROR: 'translation_function_error',
  INTERPOLATION_ERROR: 'interpolation_error',
  FORMATTER_ERROR: 'formatter_error',
  
  // 网络相关错误
  NETWORK_ERROR: 'network_error',
  TIMEOUT_ERROR: 'timeout_error',
  
  // 未知错误
  UNKNOWN_ERROR: 'unknown_error'
};

// 错误严重级别
export const ERROR_LEVELS = {
  LOW: 'low',           // 低级别：不影响核心功能
  MEDIUM: 'medium',     // 中级别：影响用户体验但不阻塞
  HIGH: 'high',         // 高级别：影响核心功能
  CRITICAL: 'critical'  // 严重级别：系统无法正常工作
};

// 错误处理策略
export const ERROR_STRATEGIES = {
  IGNORE: 'ignore',                 // 忽略错误
  LOG_ONLY: 'log_only',            // 仅记录日志
  FALLBACK: 'fallback',            // 使用降级方案
  RETRY: 'retry',                  // 重试操作
  NOTIFY_USER: 'notify_user',      // 通知用户
  RELOAD_PAGE: 'reload_page'       // 重新加载页面
};

/**
 * 国际化错误处理器类
 */
export class I18nErrorHandler {
  constructor() {
    this.errorCounts = new Map(); // 错误计数器
    this.maxRetries = 3;          // 最大重试次数
    this.isDebugMode = process.env.NODE_ENV === 'development';
  }

  /**
   * 获取错误配置
   * @param {string} errorType - 错误类型
   * @returns {Object} 错误配置
   */
  static getErrorConfig(errorType) {
    const errorConfigs = {
      [I18N_ERROR_TYPES.INITIALIZATION_FAILED]: {
        level: ERROR_LEVELS.CRITICAL,
        strategy: ERROR_STRATEGIES.FALLBACK,
        message: '国际化系统初始化失败',
        fallbackAction: 'useOriginalText'
      },
      
      [I18N_ERROR_TYPES.FRAMEWORK_LOAD_ERROR]: {
        level: ERROR_LEVELS.HIGH,
        strategy: ERROR_STRATEGIES.FALLBACK,
        message: '国际化框架加载失败',
        fallbackAction: 'useOriginalText'
      },
      
      [I18N_ERROR_TYPES.CONFIG_INVALID]: {
        level: ERROR_LEVELS.HIGH,
        strategy: ERROR_STRATEGIES.FALLBACK,
        message: '国际化配置无效',
        fallbackAction: 'useDefaultConfig'
      },
      
      [I18N_ERROR_TYPES.TRANSLATION_MISSING]: {
        level: ERROR_LEVELS.LOW,
        strategy: ERROR_STRATEGIES.FALLBACK,
        message: '翻译键缺失',
        fallbackAction: 'showTranslationKey'
      },
      
      [I18N_ERROR_TYPES.RESOURCE_LOAD_FAILED]: {
        level: ERROR_LEVELS.MEDIUM,
        strategy: ERROR_STRATEGIES.RETRY,
        message: '翻译资源加载失败',
        fallbackAction: 'useDefaultLanguage'
      },
      
      [I18N_ERROR_TYPES.LOCALE_NOT_FOUND]: {
        level: ERROR_LEVELS.MEDIUM,
        strategy: ERROR_STRATEGIES.FALLBACK,
        message: '语言包未找到',
        fallbackAction: 'useDefaultLanguage'
      },
      
      [I18N_ERROR_TYPES.LANGUAGE_NOT_SUPPORTED]: {
        level: ERROR_LEVELS.LOW,
        strategy: ERROR_STRATEGIES.FALLBACK,
        message: '不支持的语言',
        fallbackAction: 'useDefaultLanguage'
      },
      
      [I18N_ERROR_TYPES.LANGUAGE_SWITCH_FAILED]: {
        level: ERROR_LEVELS.MEDIUM,
        strategy: ERROR_STRATEGIES.RETRY,
        message: '语言切换失败',
        fallbackAction: 'keepCurrentLanguage'
      },
      
      [I18N_ERROR_TYPES.URL_PARSE_ERROR]: {
        level: ERROR_LEVELS.LOW,
        strategy: ERROR_STRATEGIES.FALLBACK,
        message: 'URL参数解析失败',
        fallbackAction: 'useDefaultLanguage'
      },
      
      [I18N_ERROR_TYPES.TRANSLATION_FUNCTION_ERROR]: {
        level: ERROR_LEVELS.MEDIUM,
        strategy: ERROR_STRATEGIES.FALLBACK,
        message: '翻译函数执行错误',
        fallbackAction: 'showOriginalText'
      },
      
      [I18N_ERROR_TYPES.INTERPOLATION_ERROR]: {
        level: ERROR_LEVELS.LOW,
        strategy: ERROR_STRATEGIES.FALLBACK,
        message: '参数插值错误',
        fallbackAction: 'showTextWithoutParams'
      },
      
      [I18N_ERROR_TYPES.FORMATTER_ERROR]: {
        level: ERROR_LEVELS.LOW,
        strategy: ERROR_STRATEGIES.FALLBACK,
        message: '格式化错误',
        fallbackAction: 'showRawText'
      },
      
      [I18N_ERROR_TYPES.NETWORK_ERROR]: {
        level: ERROR_LEVELS.MEDIUM,
        strategy: ERROR_STRATEGIES.RETRY,
        message: '网络错误',
        fallbackAction: 'useCachedData'
      },
      
      [I18N_ERROR_TYPES.TIMEOUT_ERROR]: {
        level: ERROR_LEVELS.MEDIUM,
        strategy: ERROR_STRATEGIES.RETRY,
        message: '请求超时',
        fallbackAction: 'useCachedData'
      },
      
      [I18N_ERROR_TYPES.UNKNOWN_ERROR]: {
        level: ERROR_LEVELS.MEDIUM,
        strategy: ERROR_STRATEGIES.LOG_ONLY,
        message: '未知错误',
        fallbackAction: 'continueExecution'
      }
    };

    return errorConfigs[errorType] || errorConfigs[I18N_ERROR_TYPES.UNKNOWN_ERROR];
  }

  /**
   * 增加错误计数
   * @param {string} errorType - 错误类型
   * @returns {number} 当前错误次数
   */
  incrementErrorCount(errorType) {
    const currentCount = this.errorCounts.get(errorType) || 0;
    const newCount = currentCount + 1;
    this.errorCounts.set(errorType, newCount);
    return newCount;
  }

  /**
   * 重置错误计数
   * @param {string} errorType - 错误类型
   */
  resetErrorCount(errorType) {
    this.errorCounts.delete(errorType);
  }

  /**
   * 检查是否应该重试
   * @param {string} errorType - 错误类型
   * @returns {boolean} 是否应该重试
   */
  shouldRetry(errorType) {
    const errorCount = this.errorCounts.get(errorType) || 0;
    return errorCount < this.maxRetries;
  }

  /**
   * 记录错误日志
   * @param {string} errorType - 错误类型
   * @param {Error|string} error - 错误对象或错误信息
   * @param {Object} context - 错误上下文
   */
  logError(errorType, error, context = {}) {
    const config = I18nErrorHandler.getErrorConfig(errorType);
    const errorInfo = {
      type: errorType,
      level: config.level,
      message: config.message,
      error: error instanceof Error ? error.message : error,
      stack: error instanceof Error ? error.stack : undefined,
      context: context,
      timestamp: new Date().toISOString(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Node.js',
      url: typeof window !== 'undefined' ? window.location.href : 'unknown'
    };

    // 根据错误级别选择日志方法
    switch (config.level) {
      case ERROR_LEVELS.CRITICAL:
        console.error('[I18N CRITICAL]', errorInfo);
        break;
      case ERROR_LEVELS.HIGH:
        console.error('[I18N ERROR]', errorInfo);
        break;
      case ERROR_LEVELS.MEDIUM:
        console.warn('[I18N WARNING]', errorInfo);
        break;
      case ERROR_LEVELS.LOW:
        if (this.isDebugMode) {
          console.info('[I18N INFO]', errorInfo);
        }
        break;
      default:
        console.log('[I18N LOG]', errorInfo);
    }

    // 在生产环境中可以添加错误上报逻辑
    if (process.env.NODE_ENV === 'production' && config.level !== ERROR_LEVELS.LOW) {
      this.reportError(errorInfo);
    }
  }

  /**
   * 上报错误到监控系统（生产环境）
   * @param {Object} errorInfo - 错误信息
   */
  reportError(errorInfo) {
    // 这里可以集成第三方错误监控服务
    // 例如：Sentry, Bugsnag, 自定义错误上报接口等
    try {
      // 示例：发送到自定义错误收集接口
      if (window.fetch && typeof window.fetch === 'function') {
        fetch('/api/error-report', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(errorInfo)
        }).catch(() => {
          // 错误上报失败时静默处理，不影响主流程
        });
      }
    } catch (e) {
      // 错误上报过程中的异常不应该影响主流程
    }
  }

  /**
   * 主要错误处理方法 - 处理不同类型的国际化错误
   * @param {string} errorType - 错误类型
   * @param {Error|string} error - 错误对象或错误信息
   * @param {Object} context - 错误上下文
   * @returns {any} 处理结果或降级值
   */
  static handleError(errorType, error, context = {}) {
    const handler = i18nErrorHandler;
    const config = I18nErrorHandler.getErrorConfig(errorType);
    
    // 增加错误计数
    const errorCount = handler.incrementErrorCount(errorType);
    
    // 记录错误日志
    handler.logError(errorType, error, { ...context, errorCount });
    
    // 根据错误策略处理
    switch (config.strategy) {
      case ERROR_STRATEGIES.IGNORE:
        return null;
        
      case ERROR_STRATEGIES.LOG_ONLY:
        return context.defaultValue || null;
        
      case ERROR_STRATEGIES.FALLBACK:
        return handler.handleFallback(errorType, error, context);
        
      case ERROR_STRATEGIES.RETRY:
        if (handler.shouldRetry(errorType)) {
          return handler.handleRetry(errorType, error, context);
        } else {
          // 重试次数用完，使用降级方案
          return handler.handleFallback(errorType, error, context);
        }
        
      case ERROR_STRATEGIES.NOTIFY_USER:
        handler.notifyUser(config.message, error);
        return handler.handleFallback(errorType, error, context);
        
      case ERROR_STRATEGIES.RELOAD_PAGE:
        handler.reloadPage();
        return null;
        
      default:
        return handler.handleFallback(errorType, error, context);
    }
  }

  /**
   * 处理降级方案
   * @param {string} errorType - 错误类型
   * @param {Error|string} error - 错误对象
   * @param {Object} context - 错误上下文
   * @returns {any} 降级处理结果
   */
  handleFallback(errorType, error, context) {
    const config = I18nErrorHandler.getErrorConfig(errorType);
    
    switch (config.fallbackAction) {
      case 'useOriginalText':
        return this.useOriginalText(context);
        
      case 'useDefaultConfig':
        return this.useDefaultConfig(context);
        
      case 'showTranslationKey':
        return this.showTranslationKey(context);
        
      case 'useDefaultLanguage':
        return this.useDefaultLanguage(context);
        
      case 'keepCurrentLanguage':
        return this.keepCurrentLanguage(context);
        
      case 'showOriginalText':
        return this.showOriginalText(context);
        
      case 'showTextWithoutParams':
        return this.showTextWithoutParams(context);
        
      case 'showRawText':
        return this.showRawText(context);
        
      case 'useCachedData':
        return this.useCachedData(context);
        
      case 'continueExecution':
        return context.defaultValue || null;
        
      default:
        return context.defaultValue || context.translationKey || '翻译错误';
    }
  }

  /**
   * 处理重试逻辑
   * @param {string} errorType - 错误类型
   * @param {Error|string} error - 错误对象
   * @param {Object} context - 错误上下文
   * @returns {Promise|any} 重试结果
   */
  handleRetry(errorType, error, context) {
    const retryDelay = Math.pow(2, this.errorCounts.get(errorType) || 1) * 1000; // 指数退避
    
    if (context.retryFunction && typeof context.retryFunction === 'function') {
      // 异步重试
      return new Promise((resolve) => {
        setTimeout(() => {
          try {
            const result = context.retryFunction();
            if (result instanceof Promise) {
              result
                .then(resolve)
                .catch((retryError) => {
                  resolve(this.handleFallback(errorType, retryError, context));
                });
            } else {
              resolve(result);
            }
          } catch (retryError) {
            resolve(this.handleFallback(errorType, retryError, context));
          }
        }, retryDelay);
      });
    } else {
      // 同步降级
      return this.handleFallback(errorType, error, context);
    }
  }

  /**
   * 使用原始文本（不进行翻译）
   * @param {Object} context - 上下文
   * @returns {string} 原始文本
   */
  useOriginalText(context) {
    return context.originalText || context.translationKey || '文本加载失败';
  }

  /**
   * 使用默认配置
   * @param {Object} context - 上下文
   * @returns {any} 默认配置值
   */
  useDefaultConfig() {
    return {
      locale: 'zh-CN',
      fallbackLocale: 'zh-CN',
      messages: {}
    };
  }

  /**
   * 显示翻译键（用于调试）
   * @param {Object} context - 上下文
   * @returns {string} 翻译键
   */
  showTranslationKey(context) {
    const key = context.translationKey || 'unknown.key';
    return this.isDebugMode ? `[${key}]` : key.split('.').pop() || key;
  }

  /**
   * 使用默认语言
   * @param {Object} context - 上下文
   * @returns {string} 默认语言标识
   */
  useDefaultLanguage(context) {
    if (context.i18nInstance) {
      try {
        context.i18nInstance.locale = 'zh-CN';
        return 'zh-CN';
      } catch (e) {
        // 设置失败时返回当前语言
        return context.i18nInstance.locale || 'zh-CN';
      }
    }
    return 'zh-CN';
  }

  /**
   * 保持当前语言
   * @param {Object} context - 上下文
   * @returns {string} 当前语言
   */
  keepCurrentLanguage(context) {
    if (context.i18nInstance) {
      return context.i18nInstance.locale || 'zh-CN';
    }
    return 'zh-CN';
  }

  /**
   * 显示原始文本
   * @param {Object} context - 上下文
   * @returns {string} 原始文本
   */
  showOriginalText(context) {
    return context.originalText || context.translationKey || '显示错误';
  }

  /**
   * 显示不带参数的文本
   * @param {Object} context - 上下文
   * @returns {string} 不带参数的文本
   */
  showTextWithoutParams(context) {
    let text = context.translationText || context.translationKey || '参数错误';
    // 移除参数占位符
    return text.replace(/\{[^}]+\}/g, '');
  }

  /**
   * 显示原始文本
   * @param {Object} context - 上下文
   * @returns {string} 原始文本
   */
  showRawText(context) {
    return context.rawText || context.translationKey || '格式错误';
  }

  /**
   * 使用缓存数据
   * @param {Object} context - 上下文
   * @returns {any} 缓存数据
   */
  useCachedData(context) {
    if (context.cachedData) {
      return context.cachedData;
    }
    
    // 尝试从localStorage获取缓存
    try {
      const cacheKey = context.cacheKey || 'i18n_cache';
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        return JSON.parse(cached);
      }
    } catch (e) {
      // 缓存读取失败
    }
    
    return context.defaultValue || null;
  }

  /**
   * 通知用户错误
   * @param {string} message - 错误消息
   * @param {Error|string} error - 错误对象
   */
  notifyUser(message, error) {
    // 在开发环境显示详细错误
    if (this.isDebugMode) {
      console.warn(`用户通知: ${message}`, error);
    }
    
    // 生产环境可以使用toast或其他用户友好的通知方式
    if (typeof window !== 'undefined' && window.console) {
      console.warn(message);
    }
  }

  /**
   * 重新加载页面
   */
  reloadPage() {
    if (typeof window !== 'undefined' && window.location) {
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }

  /**
   * 翻译资源加载失败的降级处理
   * @param {string} locale - 语言标识
   * @param {Error} error - 加载错误
   * @returns {Object} 降级的翻译资源
   */
  static handleResourceLoadFailure(locale, error) {
    return I18nErrorHandler.handleError(
      I18N_ERROR_TYPES.RESOURCE_LOAD_FAILED,
      error,
      {
        locale,
        defaultValue: {
          common: {
            loading: '加载中...',
            error: '出错了',
            retry: '重试'
          }
        },
        retryFunction: () => {
          // 这里可以实现重新加载资源的逻辑
          return import(`../i18n/locales/${locale}.js`);
        }
      }
    );
  }

  /**
   * 翻译键缺失时的回退显示
   * @param {string} key - 翻译键
   * @param {string} locale - 当前语言
   * @param {any} params - 翻译参数
   * @returns {string} 回退显示文本
   */
  static handleMissingTranslation(key, locale, params) {
    return I18nErrorHandler.handleError(
      I18N_ERROR_TYPES.TRANSLATION_MISSING,
      `Translation missing for key: ${key}`,
      {
        translationKey: key,
        locale,
        params,
        defaultValue: key.split('.').pop() || key
      }
    );
  }

  /**
   * 翻译函数执行错误处理
   * @param {string} key - 翻译键
   * @param {Error} error - 执行错误
   * @param {Object} context - 上下文
   * @returns {string} 错误处理结果
   */
  static handleTranslationError(key, error, context = {}) {
    return I18nErrorHandler.handleError(
      I18N_ERROR_TYPES.TRANSLATION_FUNCTION_ERROR,
      error,
      {
        translationKey: key,
        originalText: context.originalText,
        defaultValue: key.split('.').pop() || key
      }
    );
  }
}

// 导出单例实例
export const i18nErrorHandler = new I18nErrorHandler();

// 导出默认实例
export default i18nErrorHandler;